/* eslint-disable */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SovereigntyBanner from '@/components/SovereigntyBanner';

/**
 * Public access-request form. Replaces the disabled `/auth/signup`.
 * Anyone can submit; the Maker reviews each request in
 * /dashboard/subscriptions and approves with one of three modes:
 *   • Stripe checkout · Trial · Manual bank transfer
 */
export default function RequestAccess() {
  const nav = useNavigate();
  const [email, setEmail]     = useState('');
  const [name, setName]       = useState('');
  const [company, setCompany] = useState('');
  const [plan, setPlan]       = useState('monthly');
  const [message, setMessage] = useState('');
  const [busy, setBusy]       = useState(false);
  const [submitted, setSubmitted] = useState(null); // request_id when done

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      toast.error('Email and full name are required.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/subscriptions/request', {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        company: company.trim() || null,
        requested_plan_id: plan,
        message: message.trim() || null,
      });
      if (data.status === 'already_provisioned') {
        toast.info('You already have an account.', {
          description: 'Sign in with the magic-link button instead.',
        });
        setTimeout(() => nav('/login'), 1500);
        return;
      }
      setSubmitted(data.request_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit your request.');
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="request-access-page">
      <div className="relative z-10 max-w-2xl mx-auto px-5 py-16">
        <Link
          to="/"
          className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-white"
          data-testid="ra-back-home"
        >
          ← Back to home
        </Link>

        <div className="mt-10">
          <div className="ve-eyebrow text-white/60">Maker-controlled access</div>
          <h1 className="display text-3xl md:text-5xl mt-2">Request a workspace.</h1>
          <p className="mt-3 text-sm text-white/65 max-w-lg leading-relaxed">
            We onboard each customer personally. Tell us a little about you —
            our Maker will review your request within one business day and
            provision your account directly.
          </p>
        </div>

        <SovereigntyBanner size="sm" className="mt-7 w-full max-w-lg" />

        {!submitted && (
          <form onSubmit={submit} className="mt-8 space-y-5 max-w-lg" data-testid="ra-form">
            <Field label="Work email" required>
              <input
                type="email" required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="ra-email"
                placeholder="you@company.com"
                className="ra-input"
                autoComplete="email"
              />
            </Field>

            <Field label="Full name" required>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="ra-name"
                placeholder="Jane Doe"
                className="ra-input"
                autoComplete="name"
              />
            </Field>

            <Field label="Company">
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                data-testid="ra-company"
                placeholder="Acme Inc."
                className="ra-input"
                autoComplete="organization"
              />
            </Field>

            <Field label="Plan of interest">
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                data-testid="ra-plan"
                className="ra-input"
              >
                <option value="monthly">Monthly · QAR 10,000</option>
                <option value="trial">Trial (Maker decision)</option>
                <option value="custom">Custom · talk to us</option>
              </select>
            </Field>

            <Field label="Tell us what you'd use it for (optional)">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="ra-message"
                placeholder="What's the problem you're trying to solve?"
                rows={4}
                className="ra-input resize-none"
              />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid="ra-submit"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold px-4 py-3 transition-all hover:bg-[#AE52EA] hover:shadow-[0_0_36px_-4px_rgba(174,82,234,0.7)] disabled:opacity-60"
            >
              {busy ? 'Submitting…' : 'Submit request'}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>

            <p className="text-[11px] text-white/50 leading-relaxed">
              By submitting you agree to our review process. No payment is taken
              now — if approved, you'll receive payment instructions
              (Stripe / bank transfer) or trial access by email within 24 hours.
            </p>
          </form>
        )}

        {submitted && (
          <div
            className="mt-8 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.06] p-6 max-w-lg"
            data-testid="ra-success"
          >
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 size={16} />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold">Request received</span>
            </div>
            <h2 className="display text-xl mt-3 text-white">Thanks — we'll be in touch.</h2>
            <p className="mt-3 text-sm text-white/70 leading-relaxed">
              Your request <code className="text-[#AE52EA]">{submitted}</code> is in
              our Maker's queue. You'll get an email within one business day
              with next steps. We don't auto-approve — every account is
              provisioned individually for governance reasons.
            </p>
            <div className="mt-5 flex items-center gap-2 text-[11px] text-white/55">
              <ShieldCheck size={12} className="text-[#AE52EA]" />
              <span>Maker-approved access · No password ever stored</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .ra-input {
          width: 100%;
          background: rgba(0,0,0,0.45);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 14px;
          color: #FFF;
          font-family: inherit;
        }
        .ra-input::placeholder { color: rgba(255,255,255,0.30); }
        .ra-input:focus {
          outline: none;
          border-color: #8B3FC5;
          box-shadow: 0 0 0 2px rgba(139,63,197,0.25);
        }
      `}</style>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">
      {label} {required && <span className="text-[#AE52EA]">*</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
